import { useEffect, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { PortfolioCategory, PortfolioItem } from './portfolio.types';

// 헤더·포트폴리오 목록·관리자 폼이 같은 카테고리 목록을 쓰므로 한 번만 불러와 공유한다
let categoriesRequest: Promise<PortfolioCategory[]> | null = null;
const categoryListeners = new Set<() => void>();

export function fetchPortfolioCategories(): Promise<PortfolioCategory[]> {
  if (!categoriesRequest) {
    categoriesRequest = apiRequest<PortfolioCategory[]>('/api/portfolio/categories').catch((err) => {
      categoriesRequest = null;
      throw err;
    });
  }
  return categoriesRequest;
}

// 관리자가 카테고리를 수정한 뒤 호출하면, 화면에 떠 있는 헤더 메뉴 등이 새 목록으로 다시 그려진다
export function invalidatePortfolioCategories() {
  categoriesRequest = null;
  categoryListeners.forEach((listener) => listener());
}

export function usePortfolioCategories(): PortfolioCategory[] | null {
  const [categories, setCategories] = useState<PortfolioCategory[] | null>(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      fetchPortfolioCategories()
        .then((list) => active && setCategories(list))
        .catch(() => active && setCategories([]));
    };
    load();
    categoryListeners.add(load);
    return () => {
      active = false;
      categoryListeners.delete(load);
    };
  }, []);

  return categories;
}

export function fetchPortfolioItems(): Promise<PortfolioItem[]> {
  return apiRequest<PortfolioItem[]>('/api/portfolio');
}

export function registerPortfolioView(id: string): Promise<void> {
  return apiRequest<void>(`/api/portfolio/${id}/view`, { method: 'POST' }).catch(() => undefined);
}

export function getRelatedItems(items: PortfolioItem[], item: PortfolioItem, limit = 4): PortfolioItem[] {
  const sameCategory = items.filter((other) => other.id !== item.id && other.categoryId === item.categoryId);
  const rest = items.filter((other) => other.id !== item.id && other.categoryId !== item.categoryId);
  return [...sameCategory, ...rest].slice(0, limit);
}

export function getAdjacentItems(
  items: PortfolioItem[],
  item: PortfolioItem,
): { prev: PortfolioItem; next: PortfolioItem } {
  const index = items.findIndex((other) => other.id === item.id);
  const prevIndex = (index - 1 + items.length) % items.length;
  const nextIndex = (index + 1) % items.length;
  return { prev: items[prevIndex], next: items[nextIndex] };
}

// 목록·상세에 보여줄 기준가 문구. 최종 금액은 주문 후 담당자가 확정하므로 '~'를 붙인다
export function formatPortfolioPrice(item: Pick<PortfolioItem, 'price'>): string {
  return item.price === null ? '견적 문의' : `${item.price.toLocaleString('ko-KR')}원~`;
}
